<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useConfigStore } from "../stores/config.js";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "../stores/auth.js";
import { useToastStore } from "@/components/helper/toast.js";
import { isEmptyValue } from "@/utils/lib.js";
import api from "@/api/axios";
import { apiConfig } from "../../app.config.js";
import {
  useTabState,
  saveTabStateImmediate,
} from "@/composables/useTabState.js";

const containerInfoTabState = useTabState("container-info");

const configStore = useConfigStore();
const { t, locale } = useI18n();
const authStore = useAuthStore();
const toast = useToastStore();

// Logo dipecah per huruf (bukan hardcode "BTMS") biar tetap ikut
// VITE_WEBSITE_LOGO dari .env - huruf ke-2 dikasih warna accent, sama
// seperti gaya lama (dulu literally span "T" yang di-highlight).
const logoLetters = computed(() => Array.from(configStore.websiteLogo() || ""));

const activeIcon = ref("");
const showPowerMenu = ref(false);
const powerMenuPosition = ref({ top: 0, left: 0 });
const showOrderTracking = ref(false);
const orderTrackingPosition = ref({ top: 0, left: 0 });
const showUserMenu = ref(false);
const userMenuPosition = ref({ top: 0, left: 0 });
const showLanguageMenu = ref(false);
const languageMenuPosition = ref({ top: 0, left: 0 });

// container notification
const containerNotification = ref([]);

//default login dashboard shown
const DASHBOARD_NOTIFICATION_SHOWN_KEY =
  "dashboard_container_notification_shown";
const isDashboardPath = () => {
  const currentPath = window.location.hash.split("?")[0].replace("#", "");

  return (
    currentPath === "" ||
    currentPath === "/" ||
    currentPath === "dashboard" ||
    currentPath === "/dashboard"
  );
};

const handleShowDashboardContainerNotificationOnce = async () => {
  if (!isDashboardPath()) {
    return;
  }

  const alreadyShown = sessionStorage.getItem(DASHBOARD_NOTIFICATION_SHOWN_KEY);

  if (alreadyShown) {
    return;
  }

  sessionStorage.setItem(DASHBOARD_NOTIFICATION_SHOWN_KEY, "1");

  await getContainerNotification();
};
//END

// Power menu data
const powerStats = computed(() => [
  { icon: "bi bi-list-ul", label: t("header.totalOrders"), value: "102" },
  { icon: "bi bi-diagram-3", label: t("header.activeRoutes"), value: "23" },
  { icon: "bi bi-flag", label: t("header.completed"), value: "532" },
]);

// Language options
const languageOptions = ref([
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
]);

// Order tracking data
const orderData = ref({
  waybill: "WAYBIL2025021",
  customer: "PT Kolink",
  from: "Kemayoran, Jakarta",
  to: "Priok Port",
  type: "Export",
});

const orderHistory = computed(() => [
  { status: t("orderHistory.orderCreated"), completed: true },
  { status: t("orderHistory.outFromDepo"), completed: true },
  { status: t("orderHistory.arrivedFirstStop"), completed: true },
  { status: t("orderHistory.arrivedSecondStop"), completed: true },
  { status: t("orderHistory.onWayToFinalPort"), completed: true },
  { status: t("orderHistory.deliveredToDestination"), completed: false },
]);

// User menu data
const userData = ref({
  name: "",
  role: "",
  avatar: "/media/blank.png",
});

const userMenuItems = computed(() => [
  {
    icon: "bi bi-gear",
    label: t("header.profileSettings"),
    action: "profile",
  },
  {
    icon: "bi bi-box-arrow-right",
    label: t("header.logout"),
    action: "logout",
  },
]);

function clickIcon(name) {
  // Close all other menus first
  showPowerMenu.value = false;
  showOrderTracking.value = false;
  showUserMenu.value = false;
  showLanguageMenu.value = false;

  if (name === "power") {
    if (!showPowerMenu.value) {
      // Calculate position when opening menu
      const powerIcon = document.querySelector('.icon[aria-label="power"]');
      if (powerIcon) {
        const rect = powerIcon.getBoundingClientRect();
        powerMenuPosition.value = {
          top: rect.bottom + 8,
          left: rect.left + rect.width / 2,
        };
      }
    }
    showPowerMenu.value = !showPowerMenu.value;
    activeIcon.value = showPowerMenu.value ? "power" : "";
  } else if (name === "locate") {
    if (!showOrderTracking.value) {
      // Calculate position when opening order tracking
      const locateIcon = document.querySelector('.icon[aria-label="locate"]');
      if (locateIcon) {
        const rect = locateIcon.getBoundingClientRect();
        const slideoutWidth = 380; // Default width
        const viewportWidth = window.innerWidth;
        const margin = 20;

        // Calculate safe position to avoid overflow
        let left = rect.left + rect.width / 2;

        // Check if slideout would overflow on the right
        if (left + slideoutWidth / 2 > viewportWidth - margin) {
          left = viewportWidth - margin - slideoutWidth / 2;
        }

        // Check if slideout would overflow on the left
        if (left - slideoutWidth / 2 < margin) {
          left = margin + slideoutWidth / 2;
        }

        orderTrackingPosition.value = {
          top: rect.bottom + 8, // Keep original position below icon
          left: left,
        };
      }
    }
    showOrderTracking.value = !showOrderTracking.value;
    activeIcon.value = showOrderTracking.value ? "locate" : "";
  } else if (name === "user") {
    if (!showUserMenu.value) {
      // Calculate position when opening user menu
      const userIcon = document.querySelector('.icon[aria-label="user"]');
      if (userIcon) {
        const rect = userIcon.getBoundingClientRect();
        const menuWidth = 280; // User menu width
        const viewportWidth = window.innerWidth;
        const margin = 20;

        // Calculate safe position to avoid overflow
        let left = rect.left + rect.width / 2;

        // Check if menu would overflow on the right
        if (left + menuWidth / 2 > viewportWidth - margin) {
          left = viewportWidth - margin - menuWidth / 2;
        }

        // Check if menu would overflow on the left
        if (left - menuWidth / 2 < margin) {
          left = margin + menuWidth / 2;
        }

        userMenuPosition.value = {
          top: rect.bottom + 8,
          left: left,
        };
      }
    }
    showUserMenu.value = !showUserMenu.value;
    activeIcon.value = showUserMenu.value ? "user" : "";
  } else if (name === "language") {
    if (!showLanguageMenu.value) {
      // Calculate position when opening language menu
      const languageIcon = document.querySelector(
        '.icon[aria-label="language"]',
      );
      if (languageIcon) {
        const rect = languageIcon.getBoundingClientRect();
        const menuWidth = 200; // Language menu width
        const viewportWidth = window.innerWidth;
        const margin = 20;

        // Calculate safe position to avoid overflow
        let left = rect.left + rect.width / 2;

        // Check if menu would overflow on the right
        if (left + menuWidth / 2 > viewportWidth - margin) {
          left = viewportWidth - margin - menuWidth / 2;
        }

        // Check if menu would overflow on the left
        if (left - menuWidth / 2 < margin) {
          left = margin + menuWidth / 2;
        }

        languageMenuPosition.value = {
          top: rect.bottom + 8,
          left: left,
        };
      }
    }
    showLanguageMenu.value = !showLanguageMenu.value;
    activeIcon.value = showLanguageMenu.value ? "language" : "";
  } else {
    activeIcon.value = activeIcon.value === name ? "" : name;
  }
}

// Close menu when clicking outside
function handleClickOutside(event) {
  if (
    !event.target.closest(".power-menu") &&
    !event.target.closest(".order-tracking") &&
    !event.target.closest(".user-menu") &&
    !event.target.closest(".language-menu") &&
    !event.target.closest(".icon")
  ) {
    showPowerMenu.value = false;
    showOrderTracking.value = false;
    showUserMenu.value = false;
    showLanguageMenu.value = false;
    activeIcon.value = "";
  }
}

// Order tracking functions
function trackOrder() {
  console.log("Tracking order:", orderData.value.waybill);
  // Add tracking logic here
}

function showMoreDetail() {
  console.log("Showing more details for order:", orderData.value.waybill);
  // Add more detail logic here
}

// User menu functions
async function handleUserMenuAction(action) {
  if (action === "profile") {
    // Navigate to profile settings
    console.log("Opening profile settings");
  } else if (action === "logout") {
    try {
      sessionStorage.removeItem("dashboard_container_notification_shown");

      const result = await authStore.logout();
      toast.show({
        message: result?.message || "Signed out successfully.",
        type: "info",
        position: "top-right",
      });
    } catch (err) {
      toast.show({
        message: err.message || "Failed to logout.",
        type: "danger",
        position: "top-right",
      });
    }
  }
  showUserMenu.value = false;
  activeIcon.value = "";
}

// Language functions
async function changeLanguage(langCode) {
  locale.value = langCode;
  if (isEmptyValue(localStorage.getItem("preferred-language"))) {
    localStorage.setItem("preferred-language", langCode);
  } else {
    localStorage.setItem("preferred-language", langCode);
    const userInformation = JSON.parse(
      localStorage.getItem("user_information"),
    );

    if (!isEmptyValue(userInformation)) {
      const payload = {
        language: langCode,
      };

      await api.put(
        `/api/users/changeLanguage/${userInformation.users_id}`,
        payload,
      );
    }
  }
  showLanguageMenu.value = false;
  activeIcon.value = "";
}

async function getUserInformation() {
  const information = JSON.parse(localStorage.getItem("auth:user"));

  userData.value.name = information.user_information?.name;
  userData.value.role = information.role;
}

async function getContainerNotification() {
  const response = await api.get("/api/container-infos/notifications");

  const payload = response.data.data;

  if (payload.notification) {
    containerNotification.value = payload.notification;
  }

  // hapus notification lama dulu supaya tidak duplicate
  toast.clearByVariant("notification");

  if (containerNotification.value.length > 0) {
    toast.show({
      message: "Notification Container For Out",
      variant: "notification",
      type: "warning",
      timeout: 0,
      items: containerNotification.value.map((no) => {
        const container = payload.data?.find(
          (item) => item.container_no === no,
        );

        return {
          label: no,
          closing_status: container?.closing_status || null,
          container_infos_uuid: container?.container_infos_uuid || null,
          data: container || null,
        };
      }),
      onItemClick: handleContainerNotificationClick,
    });
  }
}

const getCurrentModule = () => {
  const hash = window.location.hash || "";
  return hash.replace(/^#\/?/, "").split("?")[0];
};

const isContainerInfoModule = () => {
  return getCurrentModule() === "container-info";
};

const isDashboardModule = () => {
  const module = getCurrentModule();
  return module === "" || module === "dashboard";
};

function handleContainerNotificationClick(item) {
  const uuid = item.container_infos_uuid;

  if (!uuid) return;

  if (window.setInternalNavigation) {
    window.setInternalNavigation();
  }

  const currentModule = getCurrentModule();

  containerInfoTabState.currentView = "detail";
  containerInfoTabState.selectedContainerId = uuid;
  saveTabStateImmediate();

  const targetHash = `#/container-info?view=detail&detail=${encodeURIComponent(uuid)}`;

  if (window.location.hash !== targetHash) {
    window.location.hash = targetHash;
  }

  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent("url-change", {
        detail: {
          path: "container-info",
          params: {
            view: "detail",
            detail: uuid,
          },
        },
      }),
    );

    if (currentModule === "container-info") {
      getContainerNotification();
    }
  }, 0);
}

const handleShowContainerNotification = () => {
  getContainerNotification();
};

const handleHideContainerNotification = () => {
  toast.clearByVariant("notification");
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);

  window.addEventListener(
    "show-container-notification",
    handleShowContainerNotification,
  );

  window.addEventListener(
    "hide-container-notification",
    handleHideContainerNotification,
  );

  window.addEventListener(
    "show-dashboard-container-notification-once",
    handleShowDashboardContainerNotificationOnce,
  );

  getUserInformation();

  const currentPath = window.location.hash.split("?")[0].replace("#", "");

  if (currentPath === "/container-info" || currentPath === "container-info") {
    getContainerNotification();
  }

  if (
    currentPath === "" ||
    currentPath === "/" ||
    currentPath === "dashboard" ||
    currentPath === "/dashboard"
  ) {
    handleShowDashboardContainerNotificationOnce();
  }

  const savedLanguage = localStorage.getItem("preferred-language");
  if (savedLanguage && ["en", "ko"].includes(savedLanguage)) {
    locale.value = savedLanguage;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);

  window.removeEventListener(
    "show-container-notification",
    handleShowContainerNotification,
  );

  window.removeEventListener(
    "hide-container-notification",
    handleHideContainerNotification,
  );

  window.removeEventListener(
    "show-dashboard-container-notification-once",
    handleShowDashboardContainerNotificationOnce,
  );
});

// onMounted(() => {
//   document.addEventListener("click", handleClickOutside);

//   // get user information
//   getUserInformation();

//   // get container notification
//   getContainerNotification();

//   // Load saved language preference
//   const savedLanguage = localStorage.getItem("preferred-language");
//   if (savedLanguage && ["en", "ko"].includes(savedLanguage)) {
//     locale.value = savedLanguage;
//   }
// });

// onBeforeUnmount(() => {
//   document.removeEventListener("click", handleClickOutside);
// });
//
</script>

<template>
  <header class="app-header">
    <!-- Blue bar overlaying white, with diagonal right end -->
    <div class="blue-bar"></div>

    <!-- White bar (shorter), icons on the right -->
    <div class="white-bar">
      <div class="icons">
        <div class="icon-container">
          <button
            class="icon"
            :class="{ active: activeIcon === 'language' }"
            aria-label="language"
            :aria-pressed="activeIcon === 'language'"
            @click="clickIcon('language')"
          >
            <i class="bi bi-translate"></i>
          </button>

          <!-- Language Menu -->
          <div
            v-if="showLanguageMenu"
            class="language-menu"
            :style="{
              top: languageMenuPosition.top + 'px',
              left: languageMenuPosition.left + 'px',
            }"
          >
            <div class="language-menu-content">
              <div
                v-for="(lang, index) in languageOptions"
                :key="index"
                class="language-option"
                :class="{ active: locale === lang.code }"
                @click="changeLanguage(lang.code)"
              >
                <span class="language-flag">{{ lang.flag }}</span>
                <span class="language-name">{{ lang.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="icon-container">
          <button
            class="icon"
            :class="{ active: activeIcon === 'power' }"
            aria-label="power"
            :aria-pressed="activeIcon === 'power'"
            @click="clickIcon('power')"
          >
            <i class="bi bi-lightning-charge-fill"></i>
          </button>

          <!-- Power Menu -->
          <div
            v-if="showPowerMenu"
            class="power-menu"
            :style="{
              top: powerMenuPosition.top + 'px',
              left: powerMenuPosition.left + 'px',
            }"
          >
            <div class="power-menu-content">
              <div
                v-for="(stat, index) in powerStats"
                :key="index"
                class="power-stat"
              >
                <i :class="stat.icon"></i>
                <span class="stat-value">{{ stat.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="icon-container">
          <button
            class="icon"
            :class="{ active: activeIcon === 'locate' }"
            aria-label="locate"
            :aria-pressed="activeIcon === 'locate'"
            @click="clickIcon('locate')"
          >
            <i class="bi bi-crosshair"></i>
          </button>

          <!-- Order Tracking Slideout -->
          <Teleport to="body">
            <div
              v-if="showOrderTracking"
              class="order-tracking"
              :style="{
                top: orderTrackingPosition.top + 'px',
                left: orderTrackingPosition.left + 'px',
              }"
            >
              <div class="order-tracking-content">
                <!-- Header -->
                <div class="order-header">
                  <h3 class="order-title">
                    <i class="bi bi-geo-alt-fill"></i>
                    {{ t("header.orderTracking") }}
                  </h3>
                </div>

                <!-- Scrollable Content -->
                <div class="order-content">
                  <!-- Search Section -->
                  <div class="order-search">
                    <div class="search-input-group">
                      <input
                        v-model="orderData.waybill"
                        type="text"
                        class="search-input"
                        :placeholder="t('header.enterWaybillNumber')"
                      />
                      <button class="search-btn" @click="trackOrder">
                        <i class="bi bi-search"></i>
                        {{ t("header.track") }}
                      </button>
                    </div>
                  </div>

                  <!-- Order Details -->
                  <div class="order-details">
                    <div class="section-header">
                      <div class="section-line"></div>
                      <h4 class="section-title">
                        {{ t("header.orderDetail") }}
                      </h4>
                      <div class="section-line"></div>
                    </div>

                    <div class="detail-grid">
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ t("header.customer") }}:</span
                        >
                        <span class="detail-value">{{
                          orderData.customer
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ t("header.from") }}:</span
                        >
                        <span class="detail-value">{{ orderData.from }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">{{ t("header.to") }}:</span>
                        <span class="detail-value">{{ orderData.to }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label"
                          >{{ t("header.type") }}:</span
                        >
                        <span class="detail-value">{{ orderData.type }}</span>
                      </div>
                    </div>

                    <button class="more-detail-btn" @click="showMoreDetail">
                      <i class="bi bi-info-circle"></i>
                      {{ t("header.moreDetail") }}
                    </button>
                  </div>

                  <!-- Order History -->
                  <div class="order-history">
                    <div class="section-header">
                      <div class="section-line"></div>
                      <h4 class="section-title">{{ t("header.history") }}</h4>
                      <div class="section-line"></div>
                    </div>

                    <div class="history-list">
                      <div
                        v-for="(item, index) in orderHistory"
                        :key="index"
                        class="history-item"
                        :class="{ completed: item.completed }"
                      >
                        <div class="history-bullet">
                          <i
                            v-if="item.completed"
                            class="bi bi-check-circle-fill"
                          ></i>
                          <i v-else class="bi bi-circle"></i>
                        </div>
                        <span class="history-text">{{ item.status }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Teleport>
        </div>

        <div class="icon-container">
          <button
            class="icon"
            :class="{ active: activeIcon === 'user' }"
            aria-label="user"
            :aria-pressed="activeIcon === 'user'"
            @click="clickIcon('user')"
          >
            <i class="bi bi-person-fill"></i>
          </button>

          <!-- User Menu -->
          <Teleport to="body">
            <div
              v-if="showUserMenu"
              class="user-menu"
              :style="{
                top: userMenuPosition.top + 'px',
                left: userMenuPosition.left + 'px',
              }"
            >
              <div class="user-menu-content">
                <!-- User Profile Section -->
                <div class="user-profile">
                  <div class="user-avatar">
                    <img
                      :src="userData.avatar"
                      :alt="userData.name"
                      class="avatar-image"
                    />
                  </div>
                  <div class="user-info">
                    <h3 class="user-name">{{ userData.name }}</h3>
                    <p class="user-role">{{ userData.role }}</p>
                  </div>
                </div>

                <!-- Menu Items -->
                <div class="user-menu-items">
                  <div
                    v-for="(item, index) in userMenuItems"
                    :key="index"
                    class="menu-item"
                    @click="handleUserMenuAction(item.action)"
                  >
                    <div class="menu-item-icon">
                      <i :class="item.icon"></i>
                    </div>
                    <span class="menu-item-label">{{
                      t(
                        `header.${
                          item.action === "profile"
                            ? "profileSettings"
                            : "logout"
                        }`,
                      )
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </Teleport>
        </div>
      </div>
    </div>

    <!-- Foreground left content -->
    <div class="left">
      <div class="logo">
        <span
          v-for="(letter, index) in logoLetters"
          :key="index"
          class="logo-letter"
          :class="{ t: index === 1 }"
          >{{ letter }}</span
        >
      </div>
      <label class="search"
        ><input type="text" :placeholder="t('header.search')"
      /></label>
    </div>
  </header>
</template>

<style scoped src="./style/AppHeader.css"></style>
